import { FeatureFlag } from './featureFlags';
import { groupSections, type SectionGroup } from './groupSections';

/**
 * One entry per section of the Internal Tools tab.
 *
 * Unlike {@link ../constants/adminSections}, there are no per-section permission
 * slugs: Internal Tools is gated wholesale — the page refuses anyone who is not a
 * Strawhat `admin:super-user` before it renders a tab strip at all — so a section
 * either exists everywhere the page does, or is hidden by the deploy stage or by
 * a feature flag on the caller's own organization.
 */
export interface InternalToolsSectionDefinition {
  /** Stable id, used as the tab value and the `?section=` query parameter. */
  id: string;
  label: string;
  /**
   * Optional parent tab. Sections sharing a group are shown as sub-sections
   * of one tab rather than as separate entries in a strip that already runs
   * off the side of the page. The section id stays the `?section=` value, so
   * grouping never breaks a link somebody saved.
   */
  group?: string;
  /**
   * Sections that must not exist in production. Today only demo agency setup,
   * whose API is unmounted in prod as well.
   */
  nonProdOnly: boolean;
  /**
   * A WorkOS feature flag the caller's organization must carry, for a section
   * whose API is gated by the same flag. Omitted where the page's own
   * Strawhat gate is the whole answer.
   */
  featureFlag?: string;
}

export const INTERNAL_TOOLS_SECTIONS: readonly InternalToolsSectionDefinition[] =
  [
    {
      // First, and so the tab the page opens on. The two sections that used
      // to lead are each conditional -- Demo Sandboxes is non-prod only and
      // Agency Data Fill is behind a flag -- so the landing tab depended on
      // the stage and on who was looking.
      id: 'agencies-users',
      label: 'Agencies & Users',
      nonProdOnly: false,
    },
    {
      id: 'clinician-directory',
      label: 'Clinician Directory',
      nonProdOnly: false,
    },
    {
      id: 'demo-environments',
      label: 'Demo Sandboxes',
      nonProdOnly: true,
    },
    {
      id: 'agency-data-fill',
      label: 'Agency Data Fill',
      // Production too: the tab reads what the nightly sync did for every
      // agency, which is a production question before it is a demo one.
      nonProdOnly: false,
      featureFlag: FeatureFlag.KINNSER_AGENCY_SYNC_UI,
    },
    {
      id: 'product-updates',
      label: 'Product Updates',
      nonProdOnly: false,
    },
    {
      id: 'stedi',
      label: 'Stedi',
      nonProdOnly: false,
    },
    {
      id: 'evv',
      label: 'EVV',
      nonProdOnly: false,
    },
    {
      id: 'fax-numbers',
      label: 'Fax Numbers',
      nonProdOnly: false,
      group: 'faxes',
    },
    {
      id: 'unassigned-faxes',
      label: 'Unassigned Faxes',
      nonProdOnly: false,
      group: 'faxes',
    },
    {
      id: 'simulated-order-faxes',
      label: 'Simulated Order Faxes',
      // Not `nonProdOnly`, which asks about the deploy stage. What decides
      // whether anything is stored is whether the deployment may use the fax
      // account, and those are not the same question: a stage can be prod and
      // still have no line. The panel asks the API and says so when the answer
      // is that this stage faxes for real.
      nonProdOnly: false,
      group: 'faxes',
    },
    {
      id: 'payors',
      label: 'Payors',
      nonProdOnly: false,
    },
    {
      id: 'temporal-scaling',
      label: 'Temporal Scaling',
      nonProdOnly: false,
    },
  ];

/** Display name for each parent tab. */
export const INTERNAL_TOOLS_GROUP_LABELS: Record<string, string> = {
  faxes: 'Faxes',
};

export type InternalToolsGroup = SectionGroup<InternalToolsSectionDefinition>;

/**
 * The sections arranged into top-level tabs, in their original order.
 *
 * A section with no group becomes a tab of one, so the caller renders every
 * tab the same way instead of branching on whether a group exists.
 */
export function groupInternalToolsSections(
  sections: InternalToolsSectionDefinition[]
): InternalToolsGroup[] {
  return groupSections(sections, INTERNAL_TOOLS_GROUP_LABELS);
}

/**
 * The sections this deploy stage and this caller get, in tab order.
 *
 * Takes the answers rather than asking for them: the caller passes the stage
 * predicate and its own flags, so this stays a pure function of its arguments,
 * testable without stubbing the deploy-stage module or the auth store.
 *
 * ``flags`` is required rather than defaulted. A default would have to be
 * "every flag" or "none", and either one silently decides a gate for a caller
 * that forgot to pass anything.
 */
export function visibleInternalToolsSections(
  isNonProd: boolean,
  flags: readonly string[]
): InternalToolsSectionDefinition[] {
  return INTERNAL_TOOLS_SECTIONS.filter(
    section =>
      (!section.nonProdOnly || isNonProd) &&
      (!section.featureFlag || flags.includes(section.featureFlag))
  );
}
